/**
 * SEO Component - Dynamic meta tags for each page
 *
 * Uses react-helmet-async to manage document head
 * Supports Open Graph, Twitter Cards, and structured data
 *
 * @example
 * <SEO
 *   title="Meeting Simulator"
 *   description="Practice English meetings with AI"
 *   path="/meeting-simulator"
 * />
 */

import { Helmet } from "react-helmet-async";

interface SEOProps {
  /**
   * Page title (will be appended with site name)
   */
  title?: string;

  /**
   * Page description for meta tags
   */
  description?: string;

  /**
   * Canonical URL path (e.g., "/meeting-simulator")
   */
  path?: string;

  /**
   * Open Graph image URL (defaults to og-image.png)
   */
  image?: string;

  /**
   * Page type for Open Graph
   */
  type?: "website" | "article" | "product";

  /**
   * Disable indexing for this page
   */
  noIndex?: boolean;

  /**
   * Additional structured data (JSON-LD)
   */
  structuredData?: object;

  /**
   * Article-specific metadata
   */
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
}

const SITE_NAME = "GlobalCoders";
const BASE_URL = "https://globalcoders.app";
const DEFAULT_DESCRIPTION =
  "Master professional English communication with AI-powered conversation simulations. Practice meetings, standups, code reviews, and interviews in a safe environment.";
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;

export const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  image = DEFAULT_IMAGE,
  type = "website",
  noIndex = false,
  structuredData,
  article,
}: SEOProps) => {
  const fullTitle = title
    ? `${title} | ${SITE_NAME}`
    : `${SITE_NAME} - AI English Conversation Practice`;
  const canonicalUrl = `${BASE_URL}${path}`;
  const imageUrl = image.startsWith("http") ? image : `${BASE_URL}${image}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:url" content={canonicalUrl} />

      {/* Article-specific meta tags */}
      {article && (
        <>
          {article.publishedTime && (
            <meta
              property="article:published_time"
              content={article.publishedTime}
            />
          )}
          {article.modifiedTime && (
            <meta
              property="article:modified_time"
              content={article.modifiedTime}
            />
          )}
          {article.author && (
            <meta property="article:author" content={article.author} />
          )}
          {article.section && (
            <meta property="article:section" content={article.section} />
          )}
          {article.tags?.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Custom Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

/**
 * Pre-configured SEO for common pages
 */
export const SEOPresets = {
  home: (
    <SEO
      title="AI English Conversation Practice for Tech Professionals"
      description="Master professional English communication with AI-powered conversation simulations. Practice meetings, standups, code reviews, and interviews in a safe environment. Get instant feedback and build confidence."
      path="/"
    />
  ),

  meetingSimulator: (
    <SEO
      title="Meeting Simulator"
      description="Practice English meetings with our AI-powered simulator. Choose from 50+ realistic scenarios including daily standups, code reviews, performance reviews, and more."
      path="/meeting-simulator"
    />
  ),

  forBusinesses: (
    <SEO
      title="For Businesses"
      description="Improve your team's English communication skills with GlobalCoders. AI-powered training for tech teams, with progress tracking and real-world scenarios."
      path="/for-businesses"
    />
  ),

  faq: (
    <SEO
      title="FAQ"
      description="Frequently asked questions about GlobalCoders. Learn how our AI-powered English practice platform works, pricing, and features."
      path="/faq"
      structuredData={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [],
      }}
    />
  ),

  login: (
    <SEO
      title="Login"
      description="Sign in to GlobalCoders to continue practicing your English conversation skills."
      path="/login"
      noIndex={true}
    />
  ),

  dashboard: (
    <SEO
      title="Dashboard"
      description="Your GlobalCoders dashboard. Track your progress and continue practicing."
      path="/dashboard"
      noIndex={true}
    />
  ),

  profile: (
    <SEO
      title="Profile"
      description="Manage your GlobalCoders profile and settings."
      path="/profile"
      noIndex={true}
    />
  ),

  privacyPolicy: (
    <SEO
      title="Privacy Policy"
      description="GlobalCoders Privacy Policy. Learn how we collect, use, and protect your personal information."
      path="/privacy-policy"
    />
  ),

  termsOfService: (
    <SEO
      title="Terms of Service"
      description="GlobalCoders Terms of Service. Read our terms and conditions for using the platform."
      path="/terms-of-service"
    />
  ),

  subscriptionBilling: (
    <SEO
      title="Subscription & Billing"
      description="GlobalCoders subscription and billing terms. Learn about our pricing plans and payment options."
      path="/subscription-billing"
    />
  ),

  pricing: (
    <SEO
      title="Pricing"
      description="Choose your GlobalCoders plan. Unlock unlimited AI-powered English conversation practice with our premium subscription."
      path="/pricing"
    />
  ),

  notFound: (
    <SEO
      title="Page Not Found"
      description="The page you're looking for doesn't exist. Return to GlobalCoders homepage."
      noIndex={true}
    />
  ),
};

export default SEO;
